import { useQuery } from "@tanstack/react-query";

import { API_CACHE_KEYS } from "api/cacheKeys";
import { useCustomMutation } from "api/useCustomMutation";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import {
  CreateDetectorInput,
  Detector,
  DetectorPipelineDetails,
  PaginatedPipelineResponse,
  PipelineModelDetails,
} from "types/detectors";

import { BaseQueryOptions, CachePolicy } from "../types";

// Hook for fetching a specific detector by ID
export const useGetDetector = (
  detectorId: string,
  options: BaseQueryOptions<Detector> = {}
) => useCustomQuery<Detector>(
  API_CACHE_KEYS.detector.detail(detectorId),
  `${JANZU_URL}reef-api/detectors/${detectorId}`,
  {
    retry: 2,
    enabled: !!detectorId, // Only fetch if detectorId is provided
    refetchOnMount: true,
    shouldShowErrorSnackbar: () => false, // Disable automatic error snackbar
    ...options,
  }
);

// Hook for creating a detector
export const useCreateDetector = () => useCustomMutation<Detector, CreateDetectorInput>(
  `${JANZU_URL}device-api/predictors`,
  "POST",
  {
    cachePolicy: CachePolicy.NO_CACHE,
  }
);

// Hook for deleting a detector - using useCustomMutation with dynamic URL
export const useDeleteDetector = () => useCustomMutation<void, string>(
  (id) => `${JANZU_URL}device-api/predictors/${id}`,
  "DELETE",
  {
    cachePolicy: CachePolicy.NO_CACHE,
  }
);

// Pagination requires sequential fetches - each page URL comes from the previous response
// eslint-disable-next-line no-await-in-loop
async function fetchAllPipelines(detectorId: string): Promise<PipelineModelDetails[]> {
  const allPipelines: PipelineModelDetails[] = [];
  let url: string | null = `${JANZU_URL}device-api/v1/detectors/${detectorId}/pipelines`;

  while (url) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch pipelines: ${response.status}`);
    }
    // eslint-disable-next-line no-await-in-loop
    const data: PaginatedPipelineResponse = await response.json();
    allPipelines.push(...data.results);
    url = data.next;
  }

  return allPipelines;
}

// Hook for fetching pipeline details for a detector
export const useGetDetectorPipelineDetails = (
  detectorId: string,
  options: { enabled?: boolean } = {}
) => {
  const query = useQuery<DetectorPipelineDetails, Error>(
    API_CACHE_KEYS.detector.pipelineDetails(detectorId),
    async () => {
      const pipelines = await fetchAllPipelines(detectorId);
      return { pipelines };
    },
    {
      enabled: !!detectorId && (options.enabled !== false),
      retry: 2,
    }
  );

  return query;
};

// Combined hook that provides both create and delete operations
export const useDetector = (detectorId?: string, getOptions: BaseQueryOptions<Detector> = {}) => {
  const createDetectorMutation = useCreateDetector();
  const deleteDetectorMutation = useDeleteDetector();

  // Only use the get query if detectorId is provided
  const getDetectorQuery = useGetDetector(detectorId || "", {
    enabled: !!detectorId,
    ...getOptions,
  });

  return {
    // Get operations
    detector: getDetectorQuery.data,
    getDetector: getDetectorQuery.refetch,
    isLoadingDetector: getDetectorQuery.isFetching,
    detectorError: getDetectorQuery.error,

    // Create operations
    createDetector: createDetectorMutation.mutateAsync,
    isCreating: createDetectorMutation.isLoading,
    createError: createDetectorMutation.error,

    // Delete operations
    deleteDetector: deleteDetectorMutation,
    isDeleting: deleteDetectorMutation.isLoading,
    deleteError: deleteDetectorMutation.error,

    // Combined loading state
    isLoading: createDetectorMutation.isLoading || deleteDetectorMutation.isLoading,
  };
};
